import { Lock, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Skeleton } from "@/components/ui/skeleton";
import { isForbiddenError } from "@/utils/error.utils";

/**
 * States: what a widget shows when it has nothing, is still loading, or is not
 * the reader's to see.
 *
 * Part of the widget primitive vocabulary — see ./index.ts. Widgets differ in
 * what they measure, not in how a number, a chart or a person-row looks, so
 * every shape lives here rather than being re-invented per widget.
 */

/**
 * Uniform empty line. Widgets never invent their own wording for "nothing here",
 * and an empty widget is never mistaken for a broken one.
 */
export const WidgetEmpty = ({ message }: { message: string }) => (
  <p className="flex h-full items-center justify-center px-2 text-center text-xs text-muted-foreground">
    {message}
  </p>
);

/**
 * What a widget shows when its data could not be read — because it is not this
 * person's to see, or because the request behind it simply failed.
 *
 * Deliberately distinct from `WidgetEmpty`. "Nothing is waiting" and "this
 * couldn't be read" are different facts, and rendering the first when the
 * second is true tells an approver their queue is clear when it may be full or
 * just unreachable right now.
 *
 * **Two causes, so two faces.** The first version of this fired only on a 403,
 * because that was the ask — but every widget already had a plain `?? 0` for
 * every *other* failure (a 500, a dropped connection), so a network hiccup
 * still rendered the exact confident zero this exists to stop, and rendering
 * the same lock for both a real refusal and an ordinary failure would have
 * taught the reader that "no lock" guarantees real data, which was never true.
 * `error` lets this component tell the two apart once, here, instead of every
 * call site re-deriving it: `isForbiddenError` picks the lock and "No access";
 * anything else picks the triangle and "Couldn't load". A caller that has
 * already done its own check (or has no `error` object to hand, only a derived
 * boolean) can omit `error` and gets the forbidden face, which was this
 * component's only face before this — existing callers stay correct.
 *
 * **The visible label is always the short one.** The slots this lands in are
 * genuinely tiny: the smallest body box is 42px tall, and — because `xs` and
 * `sm` both collapse to a single column on any canvas under 1180px — as narrow
 * as ~50px of usable text width. A sentence needs ~126px there, so the first
 * version of this truncated to "No…" across the entire 420–1179px band, which
 * reads as a rendering fault rather than as a refusal. Two letters and an
 * ellipsis say less than nothing.
 *
 * So `message` is the *detail*, not the label: it goes to `title` and to
 * assistive tech, where there is no width limit, and the eye gets an icon plus
 * two words that always fit. Which part of the card is unavailable is already
 * said by where this is sitting.
 *
 * No `px` of its own — the card body already pads, and a second inset was
 * spending 16px of a ~50px line.
 */
export const WidgetNoAccess = ({
  error,
  message,
}: {
  /** The query error driving this state, when the caller has one to hand. */
  error?: unknown;
  message?: string;
}) => {
  const { t } = useTranslation("company");
  const forbidden = error === undefined || isForbiddenError(error);
  const Icon = forbidden ? Lock : TriangleAlert;
  const label = forbidden
    ? t("company:widgets.no_access")
    : t("company:widgets.couldnt_load");
  const detail =
    message ??
    (forbidden
      ? t("company:widgets.no_access_to_this_data")
      : t("company:widgets.couldnt_load_this_data"));

  return (
    <div
      title={detail}
      className="flex h-full items-center justify-center gap-1.5 text-center text-xs text-muted-foreground"
    >
      <Icon size={12} aria-hidden className="shrink-0" />
      <span aria-hidden className="min-w-0 truncate">
        {label}
      </span>
      <span className="sr-only">{detail}</span>
    </div>
  );
};

/**
 * The figure-sized stand-in for one unreadable number.
 *
 * Shared so that the three numeric primitives and the one place a count is
 * written inline as prose all refuse in the same voice — the first version of
 * this had the primitives pair an `aria-hidden` dash with an `sr-only`
 * explanation while the inline case wrote a bare "—", which screen readers
 * announce as silence. The figure was simply missing, with nothing saying it had
 * been withheld.
 *
 * `0.5em` rather than the figure's own size: at `scale="lg"` the slot is 34px,
 * and a 34px em dash is a 34px-wide hairline sitting at the same weight and
 * colour as the card's own dividers — it reads as a stray rule, or a redaction
 * bar, not as "no value". Sizing the glyph in `em` keeps it proportionate to
 * whatever slot it lands in while the parent's own line-height goes on
 * reserving the full height, so nothing reflows between this and a real number.
 */
export const NoAccessDash = () => {
  const { t } = useTranslation("company");
  return (
    <>
      <span aria-hidden className="text-[0.5em] leading-none">
        —
      </span>
      <span className="sr-only">
        {t("company:widgets.no_access_to_this_figure")}
      </span>
    </>
  );
};

/** Row skeletons for the list layouts. */
export const RowsSkeleton = ({ rows }: { rows: number }) => (
  <div className="flex flex-col gap-2">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="flex items-center gap-2">
        <Skeleton className="size-7 shrink-0 rounded-full" />
        <div className="flex-1 space-y-1">
          <Skeleton
            className="h-3 w-24 rounded"
            style={{ ["--shimmer-delay" as string]: `${index * 80}ms` }}
          />
          <Skeleton className="h-2.5 w-16 rounded" />
        </div>
      </div>
    ))}
  </div>
);
